import { computed, ref } from 'vue'
import { useFeatureFlagsStore } from '@/stores/featureFlags'
import { TenantPlan } from '@/models/Tenant'
import { CsmAction } from '@/constants/csmActions'
import { trackAction } from '@/composables/useActivityTracker'

/**
 * Single decision point for the self-serve acquisition gate (MYAC-837).
 *
 * The rule (gate at the decision point, not per-button):
 *  - Self-serve is allowed ONLY for creating a tenant on the Veille (€0) plan.
 *  - Everything else — upgrade, downgrade, resubscribe, and every top-up —
 *    routes to "Contact sales" when `self_serve_enabled` is off (its default).
 *  - Existing tenants never self-serve a plan change in either direction.
 *  - Veille (`TenantPlan.VEILLE`) ≠ the Free legacy plan (`TenantPlan.FREE`).
 *  - Flip `system/public_config.self_serve_enabled` to restore self-serve with
 *    no redeploy (regression-safe toggle).
 *
 * A per-tenant escape hatch lets specific tenants force self-serve even while
 * the global flag is off, via `Tenant.feature_flags.self_serve_enabled`
 * (resolved centrally in featureFlagsStore: tenant override ?? global; explicit
 * `false` force-disables). Backend enforces the same flags in Wave 2.
 */
export type ContactSalesScope = 'upgrade' | 'topup' | 'connect_td'

// Module-level singletons: one shared Contact-sales dialog state for the whole
// app, hosted once in App.vue. Avoids nesting a PrimeVue dialog inside another
// modal (e.g. PlanSwitchDialog), which renders it trapped behind the parent.
const contactSalesVisible = ref(false)
const contactSalesScope = ref<ContactSalesScope>('upgrade')

export function useSelfServeGate() {
  const ff = useFeatureFlagsStore()

  /**
   * Effective self-serve flag: per-tenant `feature_flags.self_serve_enabled`
   * override falling back to the global `public_config.self_serve_enabled`
   * (default OFF). Resolved in featureFlagsStore; `currentTenant === null`
   * (e.g. at /setup) → global.
   */
  const selfServeEnabled = computed<boolean>(() => ff.isSelfServeEnabled)

  /**
   * Tenant CREATION only (setup): self-serve is allowed for the Veille (€0)
   * plan and nothing else. Veille (`TenantPlan.VEILLE`, a €0 credit plan) is NOT
   * the Free legacy plan (`TenantPlan.FREE`) — they are distinct, and only
   * Veille is self-servable at creation.
   */
  function canSelfServeCreateTenant(target: TenantPlan | string | null): boolean {
    if (selfServeEnabled.value) return true
    return target === TenantPlan.VEILLE
  }

  function canSelfServeTopup(): boolean {
    return selfServeEnabled.value
  }

  /**
   * Any plan change for an EXISTING tenant — upgrade, downgrade, or resubscribe —
   * is never self-serve while gated; it always routes to Contact sales. Self-
   * serve is limited to Veille tenant creation, so there is no plan inspection
   * here: every existing tenant is entry-gated when the flag is off.
   */
  function shouldContactSalesForPlanSwitch(): boolean {
    return !selfServeEnabled.value
  }

  /** Fire the scoped CSM intent event when routing a user to Contact sales.
   * MYAC-1148: `'connect_td'` is treated exactly like `'upgrade'` here — same
   * CSM event — only `ContactSalesDialog`'s subtitle differs between them. */
  function trackContactSales(scope: ContactSalesScope): void {
    trackAction(
      scope === 'topup'
        ? CsmAction.BillingSalesContactTopup
        : CsmAction.BillingSalesContactUpgrade,
    )
  }

  /** Open the shared Contact-sales dialog and fire the scoped CSM event. */
  function openContactSales(scope: ContactSalesScope): void {
    contactSalesScope.value = scope
    contactSalesVisible.value = true
    trackContactSales(scope)
  }

  return {
    selfServeEnabled,
    canSelfServeCreateTenant,
    canSelfServeTopup,
    shouldContactSalesForPlanSwitch,
    trackContactSales,
    openContactSales,
    contactSalesVisible,
    contactSalesScope,
  }
}
